import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ShareImageButton } from "@/components/ShareCard";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const monthLabel = (m, loc) => new Date(`${m}-01T00:00:00`).toLocaleDateString(loc, { month: "long", year: "numeric" });

const drawCert = async (a) => {
  await document.fonts?.ready;
  const W = 1080, H = 1080, c = document.createElement("canvas");
  c.width = W; c.height = H;
  const x = c.getContext("2d");
  x.fillStyle = "#0A192F"; x.fillRect(0, 0, W, H);
  x.strokeStyle = "#FFD166"; x.lineWidth = 10; x.strokeRect(40, 40, W - 80, H - 80);
  x.lineWidth = 2; x.strokeRect(62, 62, W - 124, H - 124);
  x.textAlign = "center";
  x.fillStyle = "#FF6B6B"; x.font = "italic 700 34px Outfit, sans-serif"; x.fillText("Give Wings to Your Dreams", W / 2, 150);
  x.fillStyle = "#FFFFFF"; x.font = "800 48px Outfit, sans-serif"; x.fillText("RoboSpark", W / 2, 215);
  x.fillStyle = "#FFD166"; x.font = "900 78px Outfit, sans-serif"; x.fillText("AMBASSADOR", W / 2, 360); x.fillText("OF THE MONTH", W / 2, 445);
  x.fillStyle = "#9BE9F5"; x.font = "700 40px Outfit, sans-serif"; x.fillText(monthLabel(a.month, "en-IN"), W / 2, 515);
  x.fillStyle = "#FFFFFF"; x.font = "800 70px Outfit, sans-serif"; x.fillText(a.name, W / 2, 650);
  x.fillStyle = "rgba(255,255,255,0.75)"; x.font = "500 34px Outfit, sans-serif"; x.fillText(a.city || "", W / 2, 700);
  x.font = "500 32px Outfit, sans-serif"; x.fillText(`brought ${a.friends} friend${a.friends > 1 ? "s" : ""} to RoboSpark · ${a.badge || ""}`, W / 2, 790);
  x.fillStyle = "#FFFFFF"; x.font = "700 30px Outfit, sans-serif"; x.fillText("Rakesh Kumar", W / 2, 920);
  x.fillStyle = "rgba(255,255,255,0.6)"; x.font = "400 24px Outfit, sans-serif"; x.fillText("Director – RoboSpark PrP Pvt Ltd, Noida", W / 2, 955);
  return new Promise((res) => c.toBlob(res, "image/png"));
};

export default function AmbassadorCertificate() {
  const { token } = useParams();
  const { L } = useLang();
  const [a, setA] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/ambassador/${token}`).then((r) => setA(r.data)).catch(() => setErr("Certificate not found")); }, [token]);
  if (err) return <p className="min-h-[60vh] grid place-items-center text-red-600" data-testid="ambassador-cert-error">{err}</p>;
  if (!a) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const text = L(`Our family is RoboSpark Ambassador of the Month for ${monthLabel(a.month, "en-IN")}! Give Wings to Your Dreams. Join here: ${window.location.origin}/enroll`,
    `हमारा परिवार ${monthLabel(a.month, "hi-IN")} का RoboSpark एंबेसडर ऑफ़ द मंथ है! अपने सपनों को पंख दें। यहाँ जुड़ें: ${window.location.origin}/enroll`);
  return (
    <div className="bg-[#E8F0FE] min-h-screen py-10 px-4 print:p-0 print:bg-white" data-testid="ambassador-cert-page">
      <style>{"@media print { @page { size: A4 landscape; margin: 0 } header, footer, nav, [data-testid='simran-chat'], .no-print { display: none !important } }"}</style>
      <div className="no-print max-w-4xl mx-auto mb-5 flex flex-wrap gap-3 justify-end">
        <Button onClick={() => window.print()} className="rounded-full bg-[#0A192F] text-white" data-testid="ambassador-print-btn"><Printer className="w-4 h-4 mr-1" />{L("Print / Save as PDF", "प्रिंट / PDF सेव करें")}</Button>
        <ShareImageButton makeBlob={() => drawCert(a)} fname={`robospark-ambassador-${a.month}.png`} text={text} variant="red" testid="ambassador-share-btn"
          title={L("Share your certificate", "अपना प्रमाणपत्र शेयर करें")} label={L("Share on WhatsApp", "WhatsApp पर शेयर करें")} />
      </div>
      <div className="max-w-4xl mx-auto aspect-[1.414] bg-[#0A192F] text-white rounded-2xl print:rounded-none p-3 shadow-2xl print:shadow-none print:max-w-none print:h-screen" data-testid="ambassador-cert">
        <div className="h-full border-[6px] border-[#FFD166] rounded-xl print:rounded-none flex flex-col items-center justify-center text-center px-8">
          <div className="text-[#FF6B6B] italic font-bold text-lg">Give Wings to Your Dreams</div>
          <div className="font-black text-2xl font-['Outfit'] mt-1">RoboSpark</div>
          <div className="mt-6 text-4xl md:text-6xl font-black font-['Outfit'] text-[#FFD166] leading-none">AMBASSADOR<br />OF THE MONTH</div>
          <div className="mt-3 text-[#9BE9F5] font-bold text-lg" data-testid="ambassador-cert-month">{monthLabel(a.month, "en-IN")}</div>
          <div className="mt-6 text-sm text-slate-300">This certificate is proudly presented to</div>
          <div className="text-3xl md:text-5xl font-black font-['Outfit'] mt-1" data-testid="ambassador-cert-name">{a.name}</div>
          <div className="text-slate-300">{a.city}</div>
          <div className="mt-3 text-sm text-slate-200">for bringing <b>{a.friends}</b> friend{a.friends > 1 ? "s" : ""} to RoboSpark · <span className="text-[#FFD166] font-bold">{a.badge}</span></div>
          <div className="mt-8 font-bold">Rakesh Kumar</div>
          <div className="text-xs text-slate-400">Director – RoboSpark PrP Pvt Ltd, Noida</div>
        </div>
      </div>
    </div>
  );
}
